import { useFlag } from '@calendly/feature-check-react'
import { useOnValueChange } from '@calendly/hooks'
import { isEqual } from 'lodash-es'
import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetOrganizationUuid, useGetUserQuery } from 'common'
import { useSSEConnectedForPolling } from 'components/meetings'
import { useAdaptivePollingConfig } from 'components/meetings/hooks/use_adaptive_polling_config'
import { Periods } from 'constants/meetings'
import { useDebounce } from 'hooks/useDebounce'
import { timezoneSelector } from 'selectors/current_user'
import { useCurrentTime } from 'shared/contexts'

import { useFilterParamsWithUuids, useAncillaryMeetingData } from './hooks'
import {
  buildSupergraphInput,
  doesSupergraphLazyArgsMatchFixedVisibleRange,
  isPreviousPageLazyArgs,
  shouldUseMeetingsPageList,
  usesSplitFuturePastTimeFilters,
} from './supergraph_meeting_search_utils'
import { useLegacyMeetingSearchQueries } from './use_legacy_meeting_search_queries'
import { useSupergraphSearchDataQueries } from './use_supergraph_search_data_queries'
import { combineMeetings, groupAndMap } from './utils'
import { VISIBLE_RANGE_PAGE_SIZE } from './visible_range_constants'
import { buildMeetingsSearchSupergraphCalendarSliceInput } from '../../meeting_search_supergraph_utils'
import { setTodayQueryArgs } from '../../meetings_page_slice'
import { buildSearchFilters, mapNodeToMeeting, filterMeetingsDateGroupsBySchedulingMethods } from '../../utils'

const EMPTY_VISIBLE_RANGE = { start: null, end: null }

/**
 * cmd `listMeetings` handles the default page / scope / non-recap filters; keyword search and the
 * recap (meeting-intelligence) filter route to the search-then-filter `meetingsPageList`.
 */
const buildSupergraphLazyListInput = (uiFilters, sliceOpts) => {
  if (shouldUseMeetingsPageList(uiFilters)) {
    return buildMeetingsSearchSupergraphCalendarSliceInput(uiFilters, sliceOpts)
  }
  return buildSupergraphInput(uiFilters, sliceOpts)
}

const collectMeetingUuidsFromDateGroups = (dateGroups) => (
  new Set(dateGroups.flatMap((group) => group.events.map((event) => event.uuid)))
)

const filterMeetingEdgesExcludingUuids = (edges, existingUuids) => {
  if (!edges?.length || existingUuids.size === 0) return edges ?? []
  return edges.filter((edge) => !existingUuids.has(edge.node?.uuid))
}

const useMeetingSearchListShared = ({
  scopeType,
  scopeId,
  skip = false,
  defaultFilters,
  pollingConfig = {},
  visibleRangePollingEnabled = false,
  visibleDateRange,
  forwardOnly = false,
  presetPeriod = null,
  pivotDate = null,
}) => {
  const dispatch = useDispatch()
  const { data: currentUser } = useGetUserQuery()
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const timezone = useSelector(timezoneSelector)
  const currentTime = useCurrentTime()
  const filterParams = useFilterParamsWithUuids(defaultFilters)

  const filters = useMemo(() => {
    if (skip) return null
    const base = buildSearchFilters(scopeType, scopeId, filterParams)
    if (!base) return base
    const withPeriod = presetPeriod ? { ...base, period: presetPeriod } : base
    if (!pivotDate) return withPeriod
    return { ...withPeriod, pivotDate }
  }, [filterParams, scopeId, scopeType, skip, presetPeriod, pivotDate])

  const periodIsUpcoming = !skip && filterParams?.period === 'upcoming'
  const singleQueryPeriodPreset = Boolean(presetPeriod)
  const listOrder = presetPeriod === Periods.LAST_WEEK ? 'DESC' : 'ASC'
  // Forward-only (the Upcoming preset) only makes sense for the upcoming period: it adds a
  // `fromStartOfToday` time filter and skips the past query. If the period is anything else
  // (e.g. SearchFilterExport switches to `past` or a fixed range), ignore forward-only so the
  // remaining query keeps its correct time filter and the past leg is not wrongly disabled.
  const effectiveForwardOnly = forwardOnly && periodIsUpcoming && !singleQueryPeriodPreset
  const skipPastQuery = effectiveForwardOnly || singleQueryPeriodPreset
  // In forward-only mode the single forward query already covers today, so the dedicated
  // today *list* query is dropped. We still publish today query args below so the meeting
  // detail right rail can load today's meetings for its badge logic.
  const shouldLoadToday = periodIsUpcoming && !effectiveForwardOnly && !singleQueryPeriodPreset

  useEffect(() => {
    // `useMeetingsSearchData` always calls both legacy + SuperGraph hooks; the inactive one
    // must not overwrite the active backend's cached "today" query args.
    if (skip) return
    dispatch(setTodayQueryArgs(periodIsUpcoming && filters ? { ...filters, today: true } : null))
  }, [dispatch, skip, periodIsUpcoming, filters])

  const isPollingEnabled = pollingConfig.isPollingEnabled ?? false
  const isSSEConnectedFromHook = useSSEConnectedForPolling()
  const isSSEConnected = isPollingEnabled ? isSSEConnectedFromHook : false
  const { backgroundPollingConfig, fetchOnceNoPollingConfig } = useAdaptivePollingConfig({
    isSSEConnected,
    variant: 'list',
    enabled: isPollingEnabled,
  })

  const [isTabVisible, setIsTabVisible] = useState(
    () => (typeof document !== 'undefined' ? !document.hidden : true),
  )
  useEffect(() => {
    const handler = () => setIsTabVisible(!document.hidden)
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  const queryOptions = useMemo(() => {
    const base = {
      refetchOnMountOrArgChange: true,
      ...(isSSEConnected ? fetchOnceNoPollingConfig : backgroundPollingConfig),
    }
    return { ...base, pollingInterval: isTabVisible ? base.pollingInterval : 0 }
  }, [isSSEConnected, fetchOnceNoPollingConfig, backgroundPollingConfig, isTabVisible])

  const debouncedVisibleDateRange = useDebounce(visibleDateRange || EMPTY_VISIBLE_RANGE, 500)

  const visibleRangeQueryVariables = useMemo(() => {
    // Date presets (Today / This week / Last week) load a single bounded query; visible-range
    // polling is centered on scroll position (usually today) and would merge stale meetings.
    if (
      singleQueryPeriodPreset
      || !visibleRangePollingEnabled
      || !debouncedVisibleDateRange?.start
      || !debouncedVisibleDateRange?.end
      || !filters
    ) {
      return null
    }
    const startMoment = moment(debouncedVisibleDateRange.start)
    const endMoment = moment(debouncedVisibleDateRange.end)
    const rangeDays = endMoment.diff(startMoment, 'days')
    const now = currentTime.clone()

    if (now.diff(endMoment, 'days') > 90) return null

    let queryStart = startMoment
    let queryEnd = endMoment
    if (rangeDays > 7) {
      if (endMoment.isBefore(now, 'day')) {
        queryEnd = endMoment.clone().endOf('day')
        queryStart = queryEnd.clone().subtract(6, 'days').startOf('day')
      } else if (startMoment.isAfter(now, 'day')) {
        queryStart = startMoment.clone().startOf('day')
        queryEnd = queryStart.clone().add(6, 'days').endOf('day')
      } else {
        queryStart = now.clone().subtract(3, 'days').startOf('day')
        queryEnd = now.clone().add(3, 'days').endOf('day')
      }
    }

    return {
      ...filters,
      period: 'fixed',
      startDate: queryStart.format('YYYY-MM-DD'),
      endDate: queryEnd.format('YYYY-MM-DD'),
      order: 'ASC',
      first: VISIBLE_RANGE_PAGE_SIZE,
    }
  }, [
    singleQueryPeriodPreset,
    visibleRangePollingEnabled,
    debouncedVisibleDateRange?.start,
    debouncedVisibleDateRange?.end,
    filters,
    currentTime,
  ])

  const skipQueries = skip || !filters

  const visibleRangePollingInterval = isTabVisible ? backgroundPollingConfig.pollingInterval : 0

  return {
    currentUser,
    hasMsTeamsFlag,
    timezone,
    filters,
    shouldLoadToday,
    skip,
    skipQueries,
    visibleRangeQueryVariables,
    queryOptions,
    backgroundPollingConfig,
    visibleRangePollingInterval,
    forwardOnly: effectiveForwardOnly,
    skipPastQuery,
    singleQueryPeriodPreset,
    listOrder,
    presetPeriod,
  }
}

const mapLegacySources = (q) => ({
  futureData: q.futureSearchApi.data,
  futureLoading: q.futureSearchApi.isLoading,
  futureSuccess: q.futureSearchApi.isSuccess,
  todayData: q.todaySearchApi.data,
  todayLoading: q.todaySearchApi.isLoading,
  todaySuccess: q.todaySearchApi.isSuccess,
  pastData: q.pastSearchApi.data,
  pastLoading: q.pastSearchApi.isLoading,
  pastSuccess: q.pastSearchApi.isSuccess,
  visibleRangeData: q.visibleRangeDataSearch,
  trigger: q.triggerSearchApi,
  result: q.resultSearchApi,
  triggerVisibleRangeNext: q.triggerVisibleRangeNextSearchApi,
  visibleRangeNextResult: q.visibleRangeNextResultSearchApi,
  triggerMeeting: q.triggerMeetingSearchApi,
})

const mapSupergraphSources = (q) => ({
  futureData: q.futureSupergraph.data,
  futureLoading: q.futureSupergraph.isLoading,
  futureSuccess: q.futureSupergraph.isSuccess,
  todayData: q.todaySupergraph.data,
  todayLoading: q.todaySupergraph.isLoading,
  todaySuccess: q.todaySupergraph.isSuccess,
  pastData: q.pastSupergraph.data,
  pastLoading: q.pastSupergraph.isLoading,
  pastSuccess: q.pastSupergraph.isSuccess,
  visibleRangeData: q.visibleRangeDataSuper,
  trigger: q.triggerSupergraph,
  result: q.resultSupergraph,
  triggerVisibleRangeNext: q.triggerVisibleRangeNextSupergraph,
  visibleRangeNextResult: q.visibleRangeNextResultSupergraph,
  triggerMeeting: q.triggerMeetingSupergraph,
})

const useMeetingSearchListOrchestration = (shared, sources, isSupergraphBackend) => {
  const {
    currentUser,
    hasMsTeamsFlag,
    timezone,
    filters,
    shouldLoadToday,
    skip,
    visibleRangeQueryVariables,
    forwardOnly = false,
    singleQueryPeriodPreset = false,
    presetPeriod = null,
  } = shared

  const {
    futureData,
    futureLoading,
    futureSuccess,
    todayData,
    todayLoading,
    todaySuccess,
    pastData,
    pastLoading,
    pastSuccess,
    visibleRangeData,
    trigger,
    result,
    triggerVisibleRangeNext,
    visibleRangeNextResult,
    triggerMeeting,
  } = sources

  const [paginatedMeetings, setPaginatedMeetings] = useState([])
  const [refreshedMeetingsMap, setRefreshedMeetingsMap] = useState({})
  const [nextPagePagination, setNextPagePagination] = useState(null)
  const [previousPagePagination, setPreviousPagePagination] = useState(null)
  const [visibleRangeExtraEdges, setVisibleRangeExtraEdges] = useState([])
  const visibleRangeNextCursorRef = useRef(null)

  const prevFiltersRef = useRef(filters)
  const prevForwardOnlyRef = useRef(forwardOnly)
  const prevPresetPeriodRef = useRef(presetPeriod)
  useEffect(() => {
    // Toggling a preset changes which queries run without necessarily changing `filters`
    // (e.g. `upcoming_preset` is not a search filter). Reset lazily-loaded pages on either
    // change, otherwise pages fetched in one mode leak into the other.
    if (
      !isEqual(prevFiltersRef.current, filters)
      || prevForwardOnlyRef.current !== forwardOnly
      || prevPresetPeriodRef.current !== presetPeriod
    ) {
      setPaginatedMeetings([])
      setRefreshedMeetingsMap({})
      setNextPagePagination(null)
      setPreviousPagePagination(null)
      prevFiltersRef.current = filters
      prevForwardOnlyRef.current = forwardOnly
      prevPresetPeriodRef.current = presetPeriod
    }
  }, [filters, forwardOnly, presetPeriod])

  const prevVisibleRangeVarsRef = useRef(visibleRangeQueryVariables)
  useEffect(() => {
    if (!isEqual(prevVisibleRangeVarsRef.current, visibleRangeQueryVariables)) {
      setVisibleRangeExtraEdges([])
      visibleRangeNextCursorRef.current = null
      prevVisibleRangeVarsRef.current = visibleRangeQueryVariables
    }
  }, [visibleRangeQueryVariables])

  const visibleRangeHasNext = visibleRangeData?.meetings?.pageInfo?.hasNextPage
  const visibleRangeEndCursor = visibleRangeData?.meetings?.pageInfo?.endCursor
  const visibleRangeEdgeCount = visibleRangeData?.meetings?.edges?.length ?? 0
  const isFirstPageFull = visibleRangeEdgeCount >= VISIBLE_RANGE_PAGE_SIZE
  useEffect(() => {
    if (!visibleRangeQueryVariables || visibleRangeEndCursor == null) return
    if (!visibleRangeHasNext || !isFirstPageFull || visibleRangeNextResult.isFetching) return
    if (visibleRangeNextCursorRef.current === visibleRangeEndCursor) return
    visibleRangeNextCursorRef.current = visibleRangeEndCursor
    if (isSupergraphBackend) {
      triggerVisibleRangeNext(
        buildSupergraphLazyListInput(visibleRangeQueryVariables, {
          first: VISIBLE_RANGE_PAGE_SIZE,
          after: visibleRangeEndCursor,
          order: 'ASC',
        }),
      )
    } else {
      triggerVisibleRangeNext({ ...visibleRangeQueryVariables, after: visibleRangeEndCursor })
    }
  }, [
    visibleRangeQueryVariables,
    visibleRangeHasNext,
    visibleRangeEndCursor,
    isFirstPageFull,
    visibleRangeNextResult.isFetching,
    triggerVisibleRangeNext,
    isSupergraphBackend,
  ])

  useOnValueChange(visibleRangeNextResult.status, () => {
    if (visibleRangeNextResult.status !== 'fulfilled' || !visibleRangeNextResult.data?.meetings) return
    if (!visibleRangeQueryVariables || visibleRangeQueryVariables.period !== 'fixed') return
    if (isSupergraphBackend) {
      if (
        !doesSupergraphLazyArgsMatchFixedVisibleRange(
          visibleRangeNextResult.originalArgs,
          visibleRangeQueryVariables,
        )
      ) {
        return
      }
    } else if (
      visibleRangeNextResult.originalArgs?.period !== 'fixed'
      || visibleRangeNextResult.originalArgs?.startDate !== visibleRangeQueryVariables.startDate
      || visibleRangeNextResult.originalArgs?.endDate !== visibleRangeQueryVariables.endDate
    ) {
      return
    }
    const { edges, pageInfo } = visibleRangeNextResult.data.meetings
    setVisibleRangeExtraEdges((prev) => [...prev, edges])
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      visibleRangeNextCursorRef.current = pageInfo.endCursor
      if (isSupergraphBackend) {
        triggerVisibleRangeNext(
          buildSupergraphLazyListInput(visibleRangeQueryVariables, {
            first: VISIBLE_RANGE_PAGE_SIZE,
            after: pageInfo.endCursor,
            order: 'ASC',
          }),
        )
      } else if (visibleRangeNextResult.originalArgs) {
        triggerVisibleRangeNext({
          ...visibleRangeNextResult.originalArgs,
          after: pageInfo.endCursor,
        })
      }
    } else {
      visibleRangeNextCursorRef.current = null
    }
  })

  const baseMeetings = useMemo(() => {
    if (skip || !filters) return []

    if (futureLoading || pastLoading || (shouldLoadToday && todayLoading)) return []

    let combined = []
    const edgesFuture = futureData?.meetings?.edges
    const edgesToday = todayData?.meetings?.edges
    const edgesPast = pastData?.meetings?.edges
    const edgesVisibleRangeFirst = visibleRangeData?.meetings?.edges ?? []
    const allVisibleRangeEdges = visibleRangeExtraEdges.length
      ? [...edgesVisibleRangeFirst, ...visibleRangeExtraEdges.flat()]
      : edgesVisibleRangeFirst
    const splitFuturePast = usesSplitFuturePastTimeFilters(filters.period) && !singleQueryPeriodPreset

    if ((splitFuturePast || singleQueryPeriodPreset) && edgesFuture) {
      combined = combineMeetings(combined, groupAndMap(edgesFuture, timezone, hasMsTeamsFlag), currentUser?.uuid)
    }
    if (shouldLoadToday && edgesToday) {
      combined = combineMeetings(combined, groupAndMap(edgesToday, timezone, hasMsTeamsFlag), currentUser?.uuid)
    }
    // In forward-only mode the past query is skipped, but RTK Query can retain the last
    // fulfilled past result. Guard against merging that stale data into the forward stream.
    if (!forwardOnly && !singleQueryPeriodPreset && edgesPast) {
      combined = combineMeetings(combined, groupAndMap(edgesPast, timezone, hasMsTeamsFlag), currentUser?.uuid)
    }
    if (!singleQueryPeriodPreset) {
      const visibleRangeEdgesToMerge = filterMeetingEdgesExcludingUuids(
        allVisibleRangeEdges,
        collectMeetingUuidsFromDateGroups(combined),
      )
      if (visibleRangeEdgesToMerge.length) {
        combined = combineMeetings(
          combined,
          groupAndMap(visibleRangeEdgesToMerge, timezone, hasMsTeamsFlag),
          currentUser?.uuid,
        )
      }
    }

    return combined
  }, [
    skip,
    filters,
    futureData?.meetings?.edges,
    futureLoading,
    todayData?.meetings?.edges,
    todayLoading,
    pastData?.meetings?.edges,
    pastLoading,
    shouldLoadToday,
    forwardOnly,
    singleQueryPeriodPreset,
    visibleRangeData?.meetings?.edges,
    visibleRangeExtraEdges,
    timezone,
    hasMsTeamsFlag,
    currentUser?.uuid,
  ])

  const meetings = useMemo(() => {
    let combined = baseMeetings
    if (paginatedMeetings.length > 0) {
      combined = combineMeetings(combined, paginatedMeetings, currentUser?.uuid)
    }

    if (Object.keys(refreshedMeetingsMap).length > 0) {
      combined = combined.map((dayGroup) => ({
        ...dayGroup,
        events: dayGroup.events.map((event) => (refreshedMeetingsMap[event.uuid]
          ? { ...refreshedMeetingsMap[event.uuid], source: refreshedMeetingsMap[event.uuid].source ?? event.source }
          : event)
        )
      }))
    }

    return filterMeetingsDateGroupsBySchedulingMethods(combined, filters?.schedulingMethods)
  }, [baseMeetings, paginatedMeetings, refreshedMeetingsMap, currentUser?.uuid, filters?.schedulingMethods])

  useEffect(() => {
    if (futureData?.meetings?.pageInfo) {
      if (futureData.meetings.pageInfo.hasNextPage) {
        setNextPagePagination(futureData.meetings.pageInfo.endCursor)
      } else {
        setNextPagePagination(null)
      }
    }
  }, [futureData?.meetings?.pageInfo])

  useEffect(() => {
    if (forwardOnly || singleQueryPeriodPreset) {
      setPreviousPagePagination(null)
      return
    }
    if (pastData?.meetings?.pageInfo) {
      const pageInfo = pastData.meetings.pageInfo
      if (pageInfo.hasNextPage && pageInfo.endCursor) {
        setPreviousPagePagination(pageInfo.endCursor)
      } else {
        setPreviousPagePagination(null)
      }
    }
  }, [pastData?.meetings?.pageInfo, forwardOnly, singleQueryPeriodPreset])

  useOnValueChange(result.status, () => {
    if (!skip && result.status === 'fulfilled' && result.data?.meetings) {
      const pageInfo = result.data.meetings.pageInfo
      const hasMore = pageInfo.hasNextPage
      const cursor = pageInfo.endCursor
      const previousPage = isPreviousPageLazyArgs(result.originalArgs, isSupergraphBackend)
      if (hasMore && cursor) {
        if (previousPage) {
          setPreviousPagePagination(cursor)
        } else {
          setNextPagePagination(cursor)
        }
      } else if (previousPage) {
        setPreviousPagePagination(null)
      } else {
        setNextPagePagination(null)
      }
      setPaginatedMeetings((current) => combineMeetings(current, groupAndMap(result.data.meetings.edges, timezone, hasMsTeamsFlag))
      )
    }
  })

  const isLoading = useMemo(() => {
    if (skip) return false
    if (!filters) return true
    return futureLoading || pastLoading || (shouldLoadToday && todayLoading)
  }, [skip, filters, shouldLoadToday, futureLoading, pastLoading, todayLoading])

  const showBuffers = shared.showBuffers
  const [updateMeetings, { isLoading: ancillaryDataLoading }] = useAncillaryMeetingData({ meetings, showBuffers })

  const retrieveNextPage = useCallback(() => {
    if (!skip && nextPagePagination && !result.isFetching && filters) {
      const order = singleQueryPeriodPreset && presetPeriod === Periods.LAST_WEEK ? 'DESC' : 'ASC'
      if (isSupergraphBackend) {
        trigger(
          buildSupergraphLazyListInput(filters, {
            first: 30,
            after: nextPagePagination,
            order,
            fromStartOfToday: forwardOnly,
          }),
        )
      } else {
        trigger({
          ...filters,
          after: nextPagePagination,
          order,
          ...(forwardOnly ? { fromStartOfToday: true } : {}),
        })
      }
    }
  }, [
    nextPagePagination,
    result.isFetching,
    trigger,
    filters,
    skip,
    isSupergraphBackend,
    forwardOnly,
    singleQueryPeriodPreset,
    presetPeriod,
  ])

  const retrievePreviousPage = useCallback(() => {
    if (forwardOnly || singleQueryPeriodPreset) {
      return Promise.resolve(null)
    }
    if (!skip && previousPagePagination && !result.isFetching && filters) {
      if (isSupergraphBackend) {
        return trigger(
          buildSupergraphLazyListInput(filters, {
            past: true,
            first: 30,
            after: previousPagePagination,
            order: 'DESC',
          }),
        )
      }
      return trigger({
        ...filters,
        order: 'DESC',
        past: true,
        after: previousPagePagination,
        isPrevious: true,
      })
    }
    return Promise.resolve(null)
  }, [previousPagePagination, result.isFetching, trigger, filters, skip, isSupergraphBackend, forwardOnly, singleQueryPeriodPreset])

  const refreshMeeting = useCallback(
    async (uuid) => {
      if (skip || !uuid) {
        return
      }

      const queryFilters = filters ? { ...filters } : {}

      const response = isSupergraphBackend
        ? await triggerMeeting({ id: uuid })
        : await triggerMeeting({ ...queryFilters, uuid, _t: Date.now() })

      if (!response?.data) {
        return
      }

      const refreshedMeeting = mapNodeToMeeting(
        response.data,
        timezone,
        hasMsTeamsFlag,
      )
      if (!refreshedMeeting?.uuid) return

      setRefreshedMeetingsMap((prev) => ({
        ...prev,
        [refreshedMeeting.uuid]: refreshedMeeting,
      }))
    },
    [
      skip,
      filters,
      isSupergraphBackend,
      triggerMeeting,
      timezone,
      hasMsTeamsFlag,
    ],
  )

  const isSuccess = useMemo(
    () => {
      // The past query is skipped in forward-only / single-query preset modes.
      const pastReady = forwardOnly || singleQueryPeriodPreset || pastSuccess
      if (shouldLoadToday) {
        return futureSuccess && pastReady && todaySuccess
      }
      return futureSuccess && pastReady
    },
    [futureSuccess, pastSuccess, todaySuccess, shouldLoadToday, forwardOnly, singleQueryPeriodPreset]
  )

  return useMemo(() => {
    if (skip) {
      return null
    }

    return {
      data: updateMeetings(meetings),
      isSuccess,
      isLoading: isLoading || (ancillaryDataLoading && meetings.length === 0),
      retrieveNextPage,
      hasNext: !!nextPagePagination,
      isNextLoading: result?.isFetching
        && !isPreviousPageLazyArgs(result?.originalArgs, isSupergraphBackend),
      // In forward-only or single-query preset modes there is no "previous" concept.
      retrievePreviousPage: (forwardOnly || singleQueryPeriodPreset) ? undefined : retrievePreviousPage,
      hasPrevious: !!previousPagePagination,
      isPreviousLoading: result?.isFetching
        && isPreviousPageLazyArgs(result?.originalArgs, isSupergraphBackend),
      refreshMeeting
    }
  }, [
    skip,
    updateMeetings,
    meetings,
    isSuccess,
    isLoading,
    ancillaryDataLoading,
    retrieveNextPage,
    nextPagePagination,
    result,
    retrievePreviousPage,
    previousPagePagination,
    refreshMeeting,
    isSupergraphBackend,
    forwardOnly,
    singleQueryPeriodPreset,
  ])
}

export const useSearchDataLegacy = (props) => {
  const shared = useMeetingSearchListShared(props)
  const forwardOnly = shared.forwardOnly
  const q = useLegacyMeetingSearchQueries({
    skipQueries: shared.skipQueries,
    isSupergraphEnabled: false,
    shouldLoadToday: shared.shouldLoadToday,
    forwardOnly,
    skipPastQuery: shared.skipPastQuery,
    singleQueryPeriodPreset: shared.singleQueryPeriodPreset,
    listOrder: shared.listOrder,
    filters: shared.filters,
    visibleRangeQueryVariables: shared.visibleRangeQueryVariables,
    queryOptions: shared.queryOptions,
    backgroundPollingConfig: shared.backgroundPollingConfig,
    visibleRangePollingInterval: shared.visibleRangePollingInterval,
  })
  const sources = mapLegacySources(q)
  return useMeetingSearchListOrchestration(
    { ...shared, showBuffers: props.showBuffers, forwardOnly },
    sources,
    false,
  )
}

const useSearchDataSupergraph = (props) => {
  const shared = useMeetingSearchListShared(props)
  const forwardOnly = shared.forwardOnly
  const organizationUuid = useGetOrganizationUuid()

  const filtersForSupergraph = useMemo(
    () => (shared.filters && organizationUuid
      ? { ...shared.filters, organizationUuid }
      : shared.filters),
    [shared.filters, organizationUuid],
  )

  const visibleRangeForSupergraph = useMemo(
    () => (shared.visibleRangeQueryVariables && organizationUuid
      ? { ...shared.visibleRangeQueryVariables, organizationUuid }
      : shared.visibleRangeQueryVariables),
    [shared.visibleRangeQueryVariables, organizationUuid],
  )

  const q = useSupergraphSearchDataQueries({
    skipQueries: shared.skipQueries || !organizationUuid,
    isSupergraphEnabled: true,
    shouldLoadToday: shared.shouldLoadToday,
    forwardOnly,
    skipPastQuery: shared.skipPastQuery,
    listOrder: shared.listOrder,
    filters: filtersForSupergraph,
    visibleRangeQueryVariables: visibleRangeForSupergraph,
    queryOptions: shared.queryOptions,
    backgroundPollingConfig: shared.backgroundPollingConfig,
    visibleRangePollingInterval: shared.visibleRangePollingInterval,
  })
  const sources = mapSupergraphSources(q)

  return useMeetingSearchListOrchestration(
    {
      ...shared,
      filters: filtersForSupergraph,
      visibleRangeQueryVariables: visibleRangeForSupergraph,
      showBuffers: props.showBuffers,
      forwardOnly,
    },
    sources,
    true,
  )
}

export const useMeetingsSearchData = (props) => {
  const isSupergraphEnabled = useFlag('sa_meetings_page_with_supergraph', false)
  const legacyMeetingsData = useSearchDataLegacy({
    ...props,
    skip: props.skip || isSupergraphEnabled,
  })
  const supergraphMeetingsData = useSearchDataSupergraph({
    ...props,
    skip: props.skip || !isSupergraphEnabled,
  })
  return isSupergraphEnabled ? supergraphMeetingsData : legacyMeetingsData
}

export const useSearchData = useMeetingsSearchData
